// Helper to get site URL from environment variable
const getSiteUrl = () => {
  return import.meta.env.VITE_SITE_URL || 'https://readdy.ai';
};

export const createWebSiteStructuredData = (config?: {
  name?: string;
  description?: string;
  siteNavigationElements?: Array<{ name: string; url: string }>;
}) => {
  const siteUrl = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: config?.name || 'DMM FX完全ガイド',
    description: config?.description || 'DMM FXの口座開設方法、評判、キャンペーン情報、取引方法を徹底解説。初心者から上級者まで役立つFX取引の完全ガイド。',
    url: siteUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
};

export const createBreadcrumbStructuredData = (items: Array<{ name: string; url: string }>) => {
  const siteUrl = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${siteUrl}${item.url}`,
    })),
  };
};

export const createArticleStructuredData = (config: {
  headline: string;
  description: string;
  datePublished: string;
  dateModified: string;
  author: string;
  image: string;
  url: string;
}) => {
  const siteUrl = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: config.headline,
    description: config.description,
    image: config.image,
    datePublished: config.datePublished,
    dateModified: config.dateModified,
    author: {
      '@type': 'Person',
      name: config.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'DMM FX完全ガイド',
      logo: {
        '@type': 'ImageObject',
        url: 'https://public.readdy.ai/ai/img_res/c1730b969bac34bc4baf8fc3188d18ae.jpg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': config.url.startsWith('http') ? config.url : `${siteUrl}${config.url}`,
    },
  };
};

export const createFAQStructuredData = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

export const createHowToStructuredData = (config: {
  name: string;
  description: string;
  totalTime?: string;
  estimatedCost?: {
    currency: string;
    value: string;
  };
  tool?: string[];
  supply?: string[];
  steps: Array<{ name: string; text: string; image?: string }>;
}) => {
  const structuredData: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: config.name,
    description: config.description,
    step: config.steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      image: step.image,
    })),
  };

  if (config.totalTime) {
    structuredData.totalTime = config.totalTime;
  }

  if (config.estimatedCost) {
    structuredData.estimatedCost = {
      '@type': 'MonetaryAmount',
      currency: config.estimatedCost.currency,
      value: config.estimatedCost.value,
    };
  }

  if (config.tool && config.tool.length > 0) {
    structuredData.tool = config.tool.map(tool => ({
      '@type': 'HowToTool',
      name: tool,
    }));
  }

  if (config.supply && config.supply.length > 0) {
    structuredData.supply = config.supply.map(supply => ({
      '@type': 'HowToSupply',
      name: supply,
    }));
  }

  return structuredData;
};

export const createOrganizationStructuredData = () => {
  const siteUrl = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'DMM FX完全ガイド',
    url: siteUrl,
    logo: 'https://readdy.ai/api/search-image?query=DMM%20FX%20logo%20professional%20financial%20brand%20identity%20clean%20modern%20design&width=600&height=60&seq=logo-org&orientation=landscape',
    description: 'DMM FXの口座開設方法、評判、キャンペーン情報、取引方法を徹底解説するFX情報サイト',
    sameAs: [],
  };
};

export const createAggregateRatingStructuredData = (config: {
  itemName: string;
  ratingValue: number;
  reviewCount: number;
  bestRating?: number;
  worstRating?: number;
}) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: config.itemName,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: config.ratingValue,
      reviewCount: config.reviewCount,
      bestRating: config.bestRating || 5,
      worstRating: config.worstRating || 1,
    },
  };
};

export const createFinancialProductStructuredData = (config: {
  name: string;
  description: string;
  url: string;
  provider: string;
  category: string;
  feesAndCommissionsSpecification?: string;
  interestRate?: string;
}) => {
  const siteUrl = getSiteUrl();
  const structuredData: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'FinancialProduct',
    name: config.name,
    description: config.description,
    url: config.url.startsWith('http') ? config.url : `${siteUrl}${config.url}`,
    provider: {
      '@type': 'Organization',
      name: config.provider,
    },
    category: config.category,
  };

  if (config.feesAndCommissionsSpecification) {
    structuredData.feesAndCommissionsSpecification = config.feesAndCommissionsSpecification;
  }

  if (config.interestRate) {
    structuredData.interestRate = config.interestRate;
  }

  return structuredData;
};

export const createReviewStructuredData = (config: {
  itemName: string;
  reviewBody: string;
  reviewRating: number;
  author: string;
  datePublished: string;
  bestRating?: number;
  worstRating?: number;
}) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': 'Product',
      name: config.itemName,
    },
    reviewBody: config.reviewBody,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: config.reviewRating,
      bestRating: config.bestRating || 5,
      worstRating: config.worstRating || 1,
    },
    author: {
      '@type': 'Person',
      name: config.author,
    },
    datePublished: config.datePublished,
  };
};

export const createTableStructuredData = (config: {
  about: string;
  description?: string;
}) => {
  const structuredData: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Table',
    about: config.about,
  };

  if (config.description) {
    structuredData.description = config.description;
  }

  return structuredData;
};

export const createVideoObjectStructuredData = (config: {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string;
  contentUrl?: string;
  embedUrl?: string;
}) => {
  const structuredData: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: config.name,
    description: config.description,
    thumbnailUrl: config.thumbnailUrl,
    uploadDate: config.uploadDate,
  };

  if (config.duration) {
    structuredData.duration = config.duration;
  }

  if (config.contentUrl) {
    structuredData.contentUrl = config.contentUrl;
  }

  if (config.embedUrl) {
    structuredData.embedUrl = config.embedUrl;
  }

  return structuredData;
};

export const createItemListStructuredData = (config: {
  name?: string;
  description?: string;
  items: Array<{
    name: string;
    url: string;
    description?: string;
    image?: string;
  }>;
}) => {
  const siteUrl = getSiteUrl();
  const structuredData: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: config.items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Article',
        name: item.name,
        url: item.url.startsWith('http') ? item.url : `${siteUrl}${item.url}`,
        description: item.description,
        image: item.image,
      },
    })),
  };

  if (config.name) {
    structuredData.name = config.name;
  }

  if (config.description) {
    structuredData.description = config.description;
  }

  return structuredData;
};
